import { useEffect, useState } from 'react';

import { AccessEditor } from './AccessEditor';
import { memberDisplayName } from './MembersTable';
import { useToast } from '../../contexts/ToastContext';
import type { RoleGrant } from '../../domain/access';
import { toUserErrorMessage } from '../../lib/apiError';
import { useUpdateMemberAccess } from '../../services/api/hooks/useUpdateMemberAccess';
import type { ExtranetUserOutput } from '../../types/extranet-api.types';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { IconX } from '../incidents/icons';

interface UserAccessModalProps {
  member: ExtranetUserOutput | null;
  /** Ligne de l'appelant : on verrouille son rôle Administrateur et on masque la révocation. */
  isSelf: boolean;
  /** Nom à afficher quand le back ne renvoie pas celui du membre (sa propre ligne). */
  fallbackName: string;
  onClose: () => void;
}

/**
 * Modale d'édition des accès d'un membre (`PATCH /v1/users/:id`). Le PATCH
 * remplace **l'ensemble** des rôles et périmètres : on envoie toujours le jeu
 * complet, tel qu'édité. Le contenu est remonté à chaque changement de membre
 * (`key`) pour repartir d'un brouillon frais.
 */
export const UserAccessModal = ({ member, isSelf, fallbackName, onClose }: UserAccessModalProps) => {
  useEffect(() => {
    if (!member) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [member, onClose]);

  if (!member) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center px-4">
      <div
        onClick={onClose}
        aria-hidden
        className="absolute inset-0 bg-black/30 drawer-backdrop-in"
        style={{ backdropFilter: 'blur(2px)' }}
      />
      <UserAccessModalContent
        key={member.id}
        member={member}
        isSelf={isSelf}
        fallbackName={fallbackName}
        onClose={onClose}
      />
    </div>
  );
};

const UserAccessModalContent = ({
  member,
  isSelf,
  fallbackName,
  onClose,
}: {
  member: ExtranetUserOutput;
  isSelf: boolean;
  fallbackName: string;
  onClose: () => void;
}) => {
  const toast = useToast();
  const updateAccess = useUpdateMemberAccess();
  const [draft, setDraft] = useState<RoleGrant[]>(member.access);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const name = memberDisplayName(member, isSelf ? fallbackName : '—');

  const submit = (access: RoleGrant[]) => {
    updateAccess.mutate(
      { id: member.id, access },
      {
        onSuccess: () => {
          toast.success(
            access.length === 0 ? 'Accès révoqués' : 'Accès mis à jour',
            access.length === 0 ? `${name} n'a plus accès au portail.` : `Les accès de ${name} ont été enregistrés.`,
          );
          onClose();
        },
        onError: err => {
          const { title, description } = toUserErrorMessage(err, 'Échec de la mise à jour');
          toast.error(title, description);
          setConfirmRevoke(false);
        },
      },
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="user-access-modal-title"
      className="relative w-full max-w-lg bg-white border border-zinc-200 rounded-md shadow-lg drawer-panel-in flex flex-col max-h-[85vh]"
    >
      <header className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between gap-4">
        <div className="flex flex-col gap-0.5 min-w-0">
          <h2 id="user-access-modal-title" className="text-sm font-medium text-zinc-900 truncate">
            Accès de {name}
          </h2>
          {member.email ? <p className="text-xs text-zinc-500 truncate font-mono">{member.email}</p> : null}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
        >
          <IconX width={16} height={16} />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4">
        <p className="text-xs text-zinc-500">
          Cochez les profils à attribuer. Un profil sans périmètre donne accès à tout le compte.
        </p>
        <AccessEditor
          value={draft}
          onChange={setDraft}
          disabled={updateAccess.isPending}
          lockedRoles={isSelf ? ['Extranet_Admin'] : []}
          idPrefix={`member-${member.id}`}
        />
      </div>

      <footer className="px-6 py-3 border-t border-zinc-100 flex items-center justify-between gap-2">
        {!isSelf ? (
          <button
            type="button"
            onClick={() => setConfirmRevoke(true)}
            disabled={updateAccess.isPending || member.access.length === 0}
            className="text-xs text-red-700 hover:text-red-800 hover:underline underline-offset-2 disabled:opacity-50 disabled:no-underline"
          >
            Révoquer tous les accès
          </button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-3 text-sm text-zinc-700 bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => submit(draft)}
            disabled={updateAccess.isPending || draft.length === 0}
            className="h-9 px-4 text-sm font-medium bg-zinc-900 text-white rounded-md hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {updateAccess.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </footer>

      <ConfirmDialog
        open={confirmRevoke}
        title={`Révoquer tous les accès de ${name} ?`}
        description="Ce membre ne pourra plus se connecter au portail extranet. Ses rôles et périmètres seront supprimés."
        confirmLabel="Révoquer"
        tone="danger"
        isPending={updateAccess.isPending}
        onConfirm={() => submit([])}
        onClose={() => setConfirmRevoke(false)}
      />
    </div>
  );
};
